/* @flow */
import * as React from "react";
import idx from "idx";
import styled, { css } from "styled-components";
import Media from "react-media";
import moment from "moment";
import uniq from "lodash.uniq";
import Gettext, { withGettext } from "../common/Gettext";
import { get } from "../common/fetch";
import Select, { Options, Option } from "./Select";
import SelectWithInput from "./SelectWithInput";
import Profit from "./Profit";
import SuggestingInput from "../SuggestingInput";
import Money from "../Money";
import { tournamentCurrency } from "../Icons/Kuchka";
import { ActiveTournament } from "../Tournaments/Api";

import type { State as ReduxState } from "../../redux/reducer";
import type { Account } from "../../redux/CommonTypes";
import Tour from "../Tour";
type TradeSettings = $PropertyType<ReduxState, "remoteTradeSettings">;
type ReduxSettings = $PropertyType<ReduxState, "settings">;

const OptionsView = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: -1px;
  padding: 1px 28px 18px;

  @media (max-width: 479px) {
    width: 100%;
    align-items: center;
  }

  @media (max-width: 959px) {
    padding: 0 0 11px;
  }
`;

const OptionContainer = styled.div`
  width: 100%;

  @media (max-width: 479px) {
    margin-bottom: ${({ last }) => (last ? 0 : "4px")};

    ${({ isTourMode }) =>
      isTourMode &&
      css`
        width: auto;
        margin-left: 8px;
        margin-right: 8px;
      `};
  }
`;

const Heading = styled.h3`
  direction: ${props => props.theme.direction};
  margin-top: 15px;
  margin-bottom: 7px;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  line-height: 1;
  color: #99959d;

  @media (max-width: 959px) {
    font-size: ${({ theme }) =>
      theme.locale === "ar" ? theme.minFontSize : "12px"};
    margin-top: 9px;
    margin-bottom: 3px;
  }

  @media (max-width: 479px) {
    margin-top: 23px;
  }
`;

const Container = styled.div`
  width: 100%;
  margin: 0 auto;

  @media (max-width: 959px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex-grow: 1;
  }
`;

const MobileTourContainer = styled.div`
  :after {
    ${({ tourBorder }) =>
      tourBorder &&
      css`
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        border-radius: 6px;
        ${tourBorder.border};
        content: "";
      `};

    ${({ tourBorderAnimation }) =>
      tourBorderAnimation &&
      tourBorderAnimation.animation(tourBorderAnimation.keyframe)};
  }
`;

function formatSecondsToMinutes(seconds) {
  const sec = parseInt(seconds || 0, 10);

  if (sec < 60) return sec;
  return Math.floor(sec / 60);
}

type BidsProps = {
  tradeMode: "tournament" | "binary",
  percentBidThreshold: $PropertyType<TradeSettings, "percentBidThreshold">,
  percentBids: $PropertyType<TradeSettings, "percentBids">,
  suggestedBids: $PropertyType<TradeSettings, "bids">
};

type DynamicProps = {
  ...BidsProps,
  balance: number,
  maxBid: number
};

function calculateBids({
  balance,
  suggestedBids,
  percentBidThreshold,
  percentBids,
  maxBid,
  tradeMode
}: DynamicProps) {
  if (tradeMode === "tournament") {
    return [1, 5, 25, 50, 100, 300, 500, 1000, 10000];
  }

  // если у нас есть минимум баланса и процентная сетка
  if (percentBidThreshold && percentBids) {
    // баланс меньше threshold
    // - возвращаем обычные suggestedBids
    if (balance < percentBidThreshold) {
      return suggestedBids;
    }

    // в противном случае выстраиваем сетку
    const updatedSuggestedBids = percentBids.reduce((bids, percentage) => {
      const bid = Math.trunc((balance / 100) * percentage);

      bids.push(bid < maxBid ? bid : maxBid);

      return bids;
    }, []);

    return uniq(updatedSuggestedBids);
  }

  return suggestedBids;
}

type Props = {
  ...BidsProps,
  activeAccount: Account,
  possibleIntervals: $PropertyType<TradeSettings, "intervals">,
  currentBid: $PropertyType<ReduxSettings, "currentBid">,
  currentInterval: $PropertyType<ReduxSettings, "currentInterval">,
  tabs: any,
  stocks: any,
  onSettingsUpdate: Function,
  className?: string,
  maxBidVolume: number,
  showIncreaseBidLimitMessage: () => void,
  isDealAmountValid: boolean,
  validateDealAmount: (key: boolean) => void,
  gettext: any,
  children?: React.Node
};

function getNeighbourValues(value, list) {
  const i = list.indexOf(value);
  const l = list.length;

  if (i < 0) {
    let min = null;
    let max = null;

    list.forEach(item => {
      if (value > item) {
        min = item;
      } else if (max === null) {
        max = item;
      }
    });
    return [
      min === null ? null : Math.max(list[0], min),
      max === null ? null : Math.min(list[l - 1], max)
    ];
  } else if (i < 1) {
    return [null, l > i + 1 ? list[i + 1] : null];
  } else if (i + 1 < l) {
    return [list[i - 1], list[i + 1]];
  } else {
    return [list[i - 1], null];
  }
}

class Settings extends React.Component<Props, {}> {
  state = {
    localTimeDiff: 0,
    localTime: Date.now()
  };

  getServerTime = () => {
    return get("/api/server-time").catch(console.log);
  };

  synchronizeOurWatches = async () => {
    try {
      let serverTime = await this.getServerTime();
      let diff = Date.now() - Number(serverTime.time) * 1000;
  
      this.setState({
        localTimeDiff: diff
      });
    } catch (error) {
      console.log(error);
    }
  };

  tick = () => {
    this.setState({
      localTime: Date.now()
    });
  };

  componentDidMount() {
    this.synchronizeOurWatches();
    this.timer = setInterval(this.tick, 1000);
    if (this.props.isDealAmountValid === false) {
      this.props.validateDealAmount(true);
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  originalAmount = React.createRef(null);
  originalTime = React.createRef(null);

  render() {
    const {
      activeAccount,
      active_tournament,
      percentBids,
      suggestedBids,
      percentBidThreshold,
      possibleIntervals: intervals,
      currentBid,
      currentInterval,
      onSettingsUpdate,
      className,
      tradeMode,
      children,
      tourInterval,
      onTourIntervalChange
    } = this.props;

    const MIN_BID_VALUE = 0;
    const MAX_BID_VALUE =
      tradeMode === "tournament"
        ? 50000
        : activeAccount && activeAccount.type === "demo"
        ? Infinity
        : this.props.maxBidVolume;

    const bidsToPopulate = calculateBids({
      percentBids,
      percentBidThreshold,
      suggestedBids,
      tradeMode,
      maxBid: MAX_BID_VALUE,
      balance: activeAccount && parseFloat(activeAccount.balance)
    });

    let possibleIntervals = intervals;
    if (tradeMode === "tournament") {
      const finish_date = idx(active_tournament, t => t.value.finish_date);

      if (finish_date != null) {
        const endTime = moment(finish_date * 1000);
        const maxTime = moment
          .duration(
            endTime.diff(
              moment(this.state.localTime - this.state.localTimeDiff)
            )
          )
          .asSeconds();
        if (maxTime > 0) {
          possibleIntervals = intervals.filter(item => item < maxTime);

          if (maxTime < currentInterval) {
            onSettingsUpdate({
              currentInterval: possibleIntervals[possibleIntervals.length - 1]
            });
          }
        }
      }
    }

    const [prevBid, nextBid] = getNeighbourValues(currentBid, bidsToPopulate);
    const [prevInterval, nextInterval] = getNeighbourValues(
      currentInterval,
      possibleIntervals
    );

    return (
      <Container>
        <OptionsView className={className}>
          <Tour
            step={["selectAmount", "amountRecommendation"]}
            originalRef={this.originalAmount}
          >
            {({ next, isOriginal, isTourMode, tour }) => (
              <OptionContainer
                ref={isOriginal ? this.originalAmount : null}
                isTourMode={isTourMode}
              >
                <Media
                  query="(min-width: 480px)"
                  render={() => (
                    <Heading>
                      <Gettext>Amount</Gettext>:
                    </Heading>
                  )}
                />

                <Media query="(min-width: 960px)">
                  {matches =>
                    matches ? (
                      <SelectWithInput
                        tour={tour}
                        isOriginal={isOriginal}
                        ref={isOriginal ? this.originalAmount : null}
                        disabled={isTourMode}
                        tradeMode={tradeMode}
                        invalid={!this.props.isDealAmountValid}
                        value={currentBid.toString()}
                        onChange={value => {
                          const v = parseInt(value || 0, 10);

                          if (this.props.isDealAmountValid === false) {
                            this.props.validateDealAmount(true);
                          }

                          if (
                            !isNaN(v) &&
                            v >= MIN_BID_VALUE &&
                            v <= MAX_BID_VALUE
                          ) {
                            next(v);
                            onSettingsUpdate({ currentBid: v });
                          } else if (v > MAX_BID_VALUE) {
                            onSettingsUpdate({ currentBid: MAX_BID_VALUE });
                          }
                        }}
                      >
                        {() => (
                          <Options>
                            {bidsToPopulate &&
                              bidsToPopulate.map(bid => (
                                <Option key={bid} value={bid}>
                                  <Money
                                    currency={
                                      tradeMode === "tournament"
                                        ? tournamentCurrency
                                        : null
                                    }
                                  >
                                    {bid}
                                  </Money>
                                </Option>
                              ))}
                          </Options>
                        )}
                      </SelectWithInput>
                    ) : isOriginal ? (
                      <SuggestingInput
                        title="Amount"
                        ref={isOriginal ? this.originalAmount : null}
                        tradeMode={tradeMode}
                        invalid={!this.props.isDealAmountValid}
                        value={currentBid.toString()}
                        prevValue={prevBid}
                        nextValue={nextBid}
                        onChange={e => {
                          const v = parseInt(e.target.value || 0, 10);

                          if (this.props.isDealAmountValid === false) {
                            this.props.validateDealAmount(true);
                          }

                          if (
                            !isNaN(v) &&
                            v >= MIN_BID_VALUE &&
                            v <= MAX_BID_VALUE
                          ) {
                            onSettingsUpdate({ currentBid: v });
                            next();
                          } else if (v > MAX_BID_VALUE) {
                            onSettingsUpdate({ currentBid: MAX_BID_VALUE });
                          }
                        }}
                        useNativeSelect={true}
                        suggestedValues={bidsToPopulate}
                      />
                    ) : (
                      <div>
                        <MobileTourContainer
                          tourBorder={!isOriginal && tour && tour.borderStyles}
                          tourBorderAnimation={
                            !isOriginal &&
                            tour &&
                            tour.outside &&
                            tour.borderStyles
                          }
                          onClick={
                            tour.analyticsID !== "3001"
                              ? () => next(currentBid)
                              : null
                          }
                        >
                          <SuggestingInput
                            title="Amount"
                            disableInput={true}
                            isTourMode={isTourMode}
                            ref={isOriginal ? this.originalAmount : null}
                            tradeMode={tradeMode}
                            invalid={!this.props.isDealAmountValid}
                            value={currentBid.toString()}
                            prevValue={prevBid}
                            nextValue={nextBid}
                            onChange={e => {
                              const v = parseInt(e.target.value || 0, 10);

                              if (this.props.isDealAmountValid === false) {
                                this.props.validateDealAmount(true);
                              }

                              if (
                                !isNaN(v) &&
                                v >= MIN_BID_VALUE &&
                                v <= MAX_BID_VALUE
                              ) {
                                onSettingsUpdate({ currentBid: v });
                                next(v);
                              } else if (v > MAX_BID_VALUE) {
                                onSettingsUpdate({ currentBid: MAX_BID_VALUE });
                              }
                            }}
                            useNativeSelect={true}
                            suggestedValues={bidsToPopulate}
                          />
                        </MobileTourContainer>
                      </div>
                    )
                  }
                </Media>
              </OptionContainer>
            )}
          </Tour>

          <Tour
            step={["selectTime", "timeRecommendation"]}
            originalRef={this.originalTime}
          >
            {({ next, isOriginal, isTourMode, tour }) => (
              <OptionContainer
                ref={isOriginal ? this.originalTime : null}
                isTourMode={isTourMode}
                last={true}
              >
                <Media
                  query="(min-width: 480px)"
                  render={() => (
                    <Heading>
                      <Gettext>Time</Gettext>:
                    </Heading>
                  )}
                />
                <Media query="(min-width: 960px)">
                  {matches =>
                    matches ? (
                      <Select
                        tour={tour}
                        isOriginal={isOriginal}
                        value={
                          isOriginal
                            ? isTourMode
                              ? formatSecondsToMinutes(tourInterval)
                              : formatSecondsToMinutes(currentInterval)
                            : formatSecondsToMinutes(tourInterval)
                        }
                        onChange={value => {
                          next();
                          if (isOriginal) {
                            onSettingsUpdate({ currentInterval: value });
                          } else {
                            onTourIntervalChange(value);
                          }
                        }}
                        currentInterval={parseInt(currentInterval || 0, 10)}
                      >
                        {() => (
                          <Options>
                            {isOriginal ? (
                              possibleIntervals.map(interval => (
                                <Option key={interval} value={interval}>
                                  {formatSecondsToMinutes(interval)}{" "}
                                  {parseInt(interval || 0, 10) < 60 ? (
                                    <Gettext>sec</Gettext>
                                  ) : parseInt(interval || 0, 10) < 120 ||
                                    parseInt(interval || 0, 10) > 600 ? (
                                    <Gettext>min</Gettext>
                                  ) : (
                                    <Gettext>mins</Gettext>
                                  )}
                                </Option>
                              ))
                            ) : (
                              <React.Fragment>
                                <Option key={10} value={10}>
                                  {formatSecondsToMinutes(10)}{" "}
                                  <Gettext>sec</Gettext>
                                </Option>
                                <Option key={15} value={15}>
                                  {formatSecondsToMinutes(15)}{" "}
                                  <Gettext>sec</Gettext>
                                </Option>
                                <Option key={30} value={30}>
                                  {formatSecondsToMinutes(30)}{" "}
                                  <Gettext>sec</Gettext>
                                </Option>
                              </React.Fragment>
                            )}
                          </Options>
                        )}
                      </Select>
                    ) : isOriginal ? (
                      <SuggestingInput
                        disableInput={true}
                        valueFormatFn={value => formatSecondsToMinutes(value)}
                        value={isTourMode ? tourInterval : currentInterval}
                        prevValue={prevInterval}
                        nextValue={nextInterval}
                        onChange={e => {
                          if (e.target instanceof HTMLSelectElement) next();
                          onSettingsUpdate({
                            currentInterval: parseInt(e.target.value, 10)
                          });
                        }}
                        suffix={this.props.gettext`min`}
                        useNativeSelect={true}
                        suggestedValues={possibleIntervals}
                      />
                    ) : (
                      <div>
                        <MobileTourContainer
                          tourBorder={tour && tour.borderStyles}
                          tourBorderAnimation={
                            tour && tour.outside && tour.borderStyles
                          }
                          onClick={tour.analyticsID !== "3001" ? next : null}
                        >
                          <SuggestingInput
                            disableInput={true}
                            valueFormatFn={value =>
                              formatSecondsToMinutes(value)
                            }
                            value={tourInterval}
                            isTourMode={isTourMode}
                            prevValue={prevInterval}
                            nextValue={nextInterval}
                            onChange={e => {
                              if (e.target instanceof HTMLSelectElement) next();
                              if (isOriginal) {
                                onSettingsUpdate({
                                  currentInterval: parseInt(e.target.value, 10)
                                });
                              } else {
                                onTourIntervalChange(
                                  parseInt(e.target.value, 10)
                                );
                              }
                            }}
                            suffix={this.props.gettext`min`}
                            useNativeSelect={true}
                            suggestedValues={[10, 15, 30]}
                          />
                        </MobileTourContainer>
                      </div>
                    )
                  }
                </Media>
              </OptionContainer>
            )}
          </Tour>
        </OptionsView>

        <Media
          query="(min-width: 960px)"
          render={() => <Profit tradeMode={tradeMode} bid={currentBid} />}
        />

        {children}
      </Container>
    );
  }
}

export default withGettext(props => (
  <ActiveTournament>
    {active_tournament => {
      const key =
        props.tradeMode === "tournament"
          ? idx(active_tournament, stats => stats.value.account.id)
          : (props.activeAccount && props.activeAccount.id) || 0;

      return (
        <Settings {...props} active_tournament={active_tournament} key={key} />
      );
    }}
  </ActiveTournament>
));



// WEBPACK FOOTER //
// ./src/components/TradeControls/Settings.js